import { useMemo, useState } from 'react'
import { ActionIcon, Group, Skeleton, Stack, Text, Tooltip } from '@mantine/core'
import { faListCheck, faPaperclip } from '@fortawesome/sharp-duotone-solid-svg-icons'
import { faClipboardList, faClock, faRotateReverse, faUsers } from '@fortawesome/sharp-duotone-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useClaimDetails } from '../../hooks/useClaimDetails'
import { useClaimContacts } from '../../hooks/useClaimContacts'
import { useClaimAttachments } from '../../hooks/useClaimAttachments'
import { useAddFileNote } from '../../hooks/useAddFileNote'
import { usePresentPositionEdit } from '../../hooks/usePresentPositionEdit'
import { usePresentPositionOptions } from '../../hooks/usePresentPositionOptions'
import { ClaimHeader } from './ClaimHeader'
import { FileNoteForm } from './FileNoteForm'
import { ClaimKeyData } from './Summary/ClaimKeyData'
import { ClaimTabs } from './ClaimTabs/ClaimTabs'
import { Button } from '../Common/Button'
import { AttachmentExplorer } from '../Common/BoxAttchments/AttachmentExplorer'
import { ContactsTab } from '../Contact/ContactsTab'
import type { ClaimTabItem } from '../../types/tabs'
import type { KeyDataItem } from '../../types/keyDataItem'
import type { Attachment } from '../../types/attachment'
import type { AttachmentMode } from '../../hooks/useClaimAttachments'
import { formatDate, formatCurrency } from '../../utils/formatters'
import { isComposeMode, getHasEmailAttachments } from '../../utils/officeHelpers'

// A closed claim's present position is read-only (MSD-538). The value is compared
// case-insensitively and trimmed because MC1 pads some string fields.
const CLOSED_CLAIM_EDIT_MESSAGE = 'Present position cannot be changed on a closed claim.'

function isClaimClosed(presentPosition: string | null): boolean {
  return presentPosition?.trim().toLowerCase() === 'closed'
}

// presentPositionId is untyped in the OpenAPI spec, so coerce it to the number the
// Present Position dropdown selects by (MSD-538).
function toStatusId(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number.parseInt(raw, 10)
    return Number.isNaN(parsed) ? null : parsed
  }
  return null
}

const getAttachmentTooltipLabel = (hasClaimFolder: boolean, hasEmailAttachments: boolean): string => {
  if (hasClaimFolder && hasEmailAttachments) return 'View documents in Box'
  if (hasClaimFolder) return 'No email attachments to save'
  return 'No Box folder linked to this claim'
}

interface AttachmentExplorerConfig {
  attachments: Attachment[]
  title: string
  primarySectionTitle: string | undefined
  secondaryAttachments: Attachment[] | undefined
  secondarySectionTitle: string | undefined
  primaryActionLabel: string
}

function getAttachmentExplorerConfig(
  mode: AttachmentMode,
  emailAttachments: Attachment[],
  boxAttachments: Attachment[]
): AttachmentExplorerConfig {
  if (mode === 'save') {
    return {
      attachments: emailAttachments,
      title: 'Save Attachments',
      primarySectionTitle: 'Mail Attachments',
      secondaryAttachments: boxAttachments,
      secondarySectionTitle: 'Claim Files',
      primaryActionLabel: 'Save to Claim',
    }
  }
  return {
    attachments: boxAttachments,
    title: 'Add Attachments',
    primarySectionTitle: undefined,
    secondaryAttachments: undefined,
    secondarySectionTitle: undefined,
    primaryActionLabel: 'Add to Email',
  }
}

export interface ClaimDetailsPageProps {
  claimNumber: string
  onBack: () => void
  // Backend feature flag (EnableContactsTab) — when off, the Contacts tab and all
  // contact UI are absent and no contacts are fetched.
  showContacts: boolean
  // Backend feature flag (EnableFileNote) — when off, the "Add File Note" button and
  // the note form are absent, so there is no way into the feature.
  showFileNote: boolean
}

export function ClaimDetailsPage({ claimNumber, onBack, showContacts, showFileNote }: Readonly<ClaimDetailsPageProps>) {
  const { claim, folderId, lastUpdated, retry } = useClaimDetails(claimNumber)
  // A null claim number tells the hook to skip the fetch entirely.
  const { participants, adhocContacts, retry: retryContacts } = useClaimContacts(showContacts ? claimNumber : null)
  const [activeTab, setActiveTab] = useState('summary')
  const [attachmentOpen, setAttachmentOpen] = useState(false)
  // Pre-fill the editor with the claim's stored position and notes (MSD-538).
  const presentPositionEdit = usePresentPositionEdit(claimNumber, {
    statusId: toStatusId(claim.data?.presentPositionId),
    notes: claim.data?.presentPositionNote ?? null,
  })
  const presentPositionOptions = usePresentPositionOptions(claimNumber)
  // File Note entry (MSD-236/237). Only one note may be created per email, so a
  // successful save disables the header button for the rest of this pane session, and
  // the form stays open showing its own confirmation until the user closes it (MSD-736).
  const [fileNoteOpen, setFileNoteOpen] = useState(false)
  const [fileNoteSaved, setFileNoteSaved] = useState(false)
  const { saving: fileNoteSaving, error: fileNoteError, save: saveFileNote } = useAddFileNote(claimNumber)

  const loading = claim.loading
  const error = loading ? null : claim.error

  const mode = useMemo(() => (isComposeMode() ? 'add' : 'save'), [])
  const hasEmailAttachments = useMemo(() => getHasEmailAttachments(mode), [mode])
  const {
    emailAttachments,
    boxAttachments,
    loading: attachmentsLoading,
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    navigateTo,
    navigateBack,
    canGoBack,
    navigationPath,
    progress,
    actionError,
    successMessage,
    executeAction,
    cancelAction,
  } = useClaimAttachments(claimNumber, folderId, mode, attachmentOpen)

  if (loading) {
    return (
      <Stack gap="xs" p="xs">
        <Skeleton height={16} width="40%" />
        <Skeleton height={12} width="80%" />
        <Skeleton height={12} width="60%" />
        <Skeleton height={32} width="50%" mt="xs" />
        <Stack gap={4} mt="md">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Skeleton key={i} height={40} />
          ))}
        </Stack>
      </Stack>
    )
  }

  if (error || !claim.data) {
    return (
      <Stack gap="md" p="md" align="center" justify="center" mih={300}>
        <Text fz="sm" c="red" ta="center">
          {error || 'Failed to load claim details.'}
        </Text>
        <Group gap="xs">
          <Button label="Back" size="xs" variant="outline" onClick={onBack} />
          <Button label="Retry" size="xs" variant="filled" onClick={retry} />
        </Group>
      </Stack>
    )
  }

  const claimData = claim.data
  const hasClaimFolder = Boolean(folderId)
  const explorerConfig = getAttachmentExplorerConfig(mode, emailAttachments, boxAttachments)

  const handleAddFileNote = () => {
    // The note form and the attachment explorer share the panel area.
    setAttachmentOpen(false)
    setFileNoteOpen(true)
  }

  const handleSaveFileNote = async (description: string, detail: string) => {
    const saved = await saveFileNote(description, detail)
    // The panel deliberately stays open: the form swaps itself for a confirmation, so
    // the message lands where the user clicked Save rather than at the top of a page
    // they have scrolled away from (MSD-736). Closing it is their call.
    if (saved) setFileNoteSaved(true)
  }

  // Show the freshly saved status label once a save succeeds; the claim response
  // itself only refreshes on the next manual refresh.
  const savedStatusLabel =
    presentPositionEdit.state.status === 'success'
      ? (presentPositionOptions.data?.find((o) => o.id === presentPositionEdit.statusId)?.claimPresentPosition ?? null)
      : null

  // The Summary row is the editable one, so it shows the current status name and jumps
  // to the freshly saved label; the header shows the claim's presentPosition (MSD-538).
  const presentPositionName = savedStatusLabel ?? claimData.presentPositionName ?? null

  const keyDataItems: KeyDataItem[] = [
    {
      label: 'Present Position',
      value: presentPositionName,
      editor: {
        field: 'presentPosition',
        disabledReason: isClaimClosed(claimData.presentPosition) ? CLOSED_CLAIM_EDIT_MESSAGE : undefined,
      },
    },
    { label: 'Date of Loss', value: formatDate(claimData.dateOfLoss) },
    { label: 'Broker', value: claimData.broker?.name ?? null },
    { label: 'Policy Number', value: claimData.policyNumber ?? null },
    { label: 'Customer Ref', value: claimData.customerRef ?? null },
    { label: 'Instruction', value: formatDate(claimData.instructedDate) },
    { label: 'First Contact', value: formatDate(claimData.firstContactDate) },
    { label: 'Site Visit', value: formatDate(claimData.siteVisitDate) },
    { label: 'First Report', value: formatDate(claimData.firstReportDate) },
    { label: 'Total Reserve', value: formatCurrency(claimData.totalReserve, claimData.currency ?? undefined) },
    { label: 'Outstanding', value: formatCurrency(claimData.outstanding, claimData.currency ?? undefined) },
  ]

  const tabs: ClaimTabItem[] = [
    {
      value: 'summary',
      label: 'Summary',
      icon: faClipboardList,
      panel: (
        <ClaimKeyData
          items={keyDataItems}
          presentPositionEdit={presentPositionEdit}
          presentPositionOptions={presentPositionOptions.data}
          presentPositionOptionsLoading={presentPositionOptions.loading}
          presentPositionOptionsError={presentPositionOptions.error}
        />
      ),
    },
    {
      value: 'contacts',
      label: 'Contacts',
      icon: faUsers,
      panel: (
        <ContactsTab
          claimNumber={claimNumber}
          participants={participants.data ?? []}
          adhocContacts={adhocContacts.data ?? []}
          loading={participants.loading || adhocContacts.loading}
          error={participants.error ?? adhocContacts.error}
          onRetry={retryContacts}
        />
      ),
    },
    {
      value: 'tasks',
      label: 'Tasks',
      icon: faListCheck,
      panel: (
        <Text fz="xs" c="dimmed">
          Tasks Panel Content
        </Text>
      ),
    },
    {
      value: 'time-entry',
      label: 'Time Entry',
      icon: faClock,
      panel: (
        <Text fz="xs" c="dimmed">
          Time Entry Panel Content
        </Text>
      ),
    },
  ]

  // Tasks and Time Entry are not released yet, so only the first two tabs render;
  // Contacts is additionally gated on the EnableContactsTab feature flag.
  const releasedTabs = tabs.slice(0, 2)
  const visibleTabs = showContacts ? releasedTabs : releasedTabs.filter((tab) => tab.value !== 'contacts')

  // The panel area under the buttons shows exactly one of: the file note form,
  // the attachment explorer, or the tabs (no nested ternaries — Sonar S3358).
  const renderPanelContent = () => {
    if (showFileNote && fileNoteOpen) {
      return (
        <FileNoteForm
          saving={fileNoteSaving}
          saved={fileNoteSaved}
          apiError={fileNoteError}
          onSave={handleSaveFileNote}
          onClose={() => setFileNoteOpen(false)}
        />
      )
    }
    if (attachmentOpen) {
      return (
        <AttachmentExplorer
          {...explorerConfig}
          loading={attachmentsLoading}
          folderPath={[claimNumber, ...navigationPath]}
          onNavigate={navigateTo}
          onNavigateBack={navigateBack}
          canGoBack={canGoBack}
          selectable
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onSelectAll={toggleSelectAll}
          onPrimaryAction={executeAction}
          progress={progress ?? undefined}
          onCancelAction={cancelAction}
          actionError={actionError}
          successMessage={successMessage}
          onClose={() => setAttachmentOpen(false)}
        />
      )
    }
    return <ClaimTabs tabs={visibleTabs} value={activeTab} onTabChange={(tab) => setActiveTab(tab || 'summary')} />
  }

  return (
    <Stack
      gap="xs"
      p="xs"
      style={{ height: attachmentOpen ? '100vh' : undefined, overflow: attachmentOpen ? 'hidden' : undefined }}
    >
      <ClaimHeader
        title={claimData.lossLocation || claimData.insuredContact || claimData.primaryClient?.name || claimNumber}
        claimNumber={claimNumber}
        insuredName={claimData.insuredContact || 'N/A'}
        insurerName={claimData.primaryClient?.name || 'N/A'}
        dateOfLoss={formatDate(claimData.dateOfLoss) ?? 'N/A'}
        presentPosition={claimData.presentPosition ?? undefined}
        caseId={claimData.caseId}
        onAddFileNote={handleAddFileNote}
        fileNoteDisabled={fileNoteSaved}
        showFileNote={showFileNote}
      />
      <Group justify="flex-end" gap={4} wrap="nowrap">
        <Group gap={4} wrap="nowrap">
          <ActionIcon
            variant="subtle"
            size="xs"
            aria-label="Refresh"
            onClick={() => {
              retry()
              retryContacts()
            }}
          >
            <FontAwesomeIcon icon={faRotateReverse} />
          </ActionIcon>
          {lastUpdated && (
            <Text fz="xs" c="text.4">
              Updated {lastUpdated.toLocaleTimeString()}
            </Text>
          )}
        </Group>
      </Group>
      <Group justify="flex-end" align="flex-end" wrap="nowrap" gap="xs">
        <Stack gap={4} align="flex-end" w="fit-content">
          <Button label="Connected" variant="filled" color="success.6" size="xs" fullWidth onClick={onBack} />
          <Tooltip label={getAttachmentTooltipLabel(hasClaimFolder, hasEmailAttachments)} position="bottom" withArrow>
            <Button
              label={explorerConfig.title}
              variant="filled"
              size="xs"
              fullWidth
              disabled={!hasClaimFolder || !hasEmailAttachments}
              rightSection={<FontAwesomeIcon icon={faPaperclip} />}
              onClick={() => {
                setFileNoteOpen(false)
                setAttachmentOpen(true)
              }}
            />
          </Tooltip>
        </Stack>
      </Group>
      <div
        style={{
          backgroundColor: attachmentOpen ? 'var(--attachment-panel-bg)' : 'var(--section-bg)',
          margin: 'calc(-1 * var(--mantine-spacing-xs))',
          marginTop: 0,
          padding: 'var(--mantine-spacing-xs)',
          display: 'flex',
          flexDirection: 'column',
          flex: attachmentOpen ? 1 : undefined,
          minHeight: attachmentOpen ? 0 : undefined,
          overflow: attachmentOpen ? 'auto' : undefined,
        }}
      >
        {renderPanelContent()}
      </div>
    </Stack>
  )
}
